// Register.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser, checkEmailExists, resetPassword } from '../api/auth';
import { generateMembershipID } from '../utils/generateMembershipID';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [existingEmail, setExistingEmail] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    workplace: '',
    workplaceOther: '',
    governmentHospital: '',
    designation: '',
    designationOther: '',
    specialty: '',
    specialtyOther: '',
    country: '',
    countryOther: ''
  });

  // Dropdown options
  const titles = ['Dr.', 'Prof.', 'Mr.', 'Ms.', 'Mrs.'];
  
  const workplaces = [
    'Government MOH',
    'Private Sector',
    'Military Hospital',
    'Faculty Of Medicine',
    'KOC Hospital',
    'Other'
  ];

  const governmentHospitals = [
    'Adan Hospital',
    'Al Bahar Eye Center',
    'Farwanyia Hospital',
    'Jaber Al Ahmad Hospital',
    'Al Dukhan Eye Center',
    'Jahra Medical City'
  ];

  const designations = [
    'Registrar',
    'Senior Registrar',
    'Specialist',
    'Senior Specialist',
    'Consultant',
    'Other'
  ];

  const specialties = [
    'Retina',
    'Glaucoma',
    'Cornea',
    'Pediatric Ophthalmology',
    'Oculoplasty',
    'Neuro-Ophthalmology',
    'General Ophthalmology',
    'Uveitis',
    'Other'
  ];

  const countries = [
    'Kuwait',
    'Qatar',
    'Oman',
    'KSA',
    'UAE',
    'Bahrain',
    'Egypt',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: value
      };

      // Clear "Other" text fields when a user switches away from "Other"
      if (name === 'specialty' && value !== 'Other') next.specialtyOther = '';

      return next;
    });
    setError('');
  };

  const validateForm = () => {
    if (!formData.title) return 'Please select a title';
    if (!formData.firstName.trim()) return 'Please enter your first name';
    if (!formData.lastName.trim()) return 'Please enter your last name';
    if (!formData.email.trim()) return 'Please enter your email';
    if (!formData.phone.trim()) return 'Please enter your phone number';
    if (!formData.password) return 'Please enter a password';
    if (formData.password.length < 6) return 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    if (!formData.workplace) return 'Please select your workplace';
    if (formData.workplace === 'Government MOH' && !formData.governmentHospital) {
      return 'Please select a government hospital';
    }
    if (formData.workplace === 'Other' && !formData.workplaceOther.trim()) {
      return 'Please specify your workplace';
    }
    if (!formData.designation) return 'Please select your designation';
    if (formData.designation === 'Other' && !formData.designationOther.trim()) {
      return 'Please specify your designation';
    }
    if (!formData.specialty) return 'Please select your specialty';
    if (formData.specialty === 'Other' && !formData.specialtyOther.trim()) {
      return 'Please specify your specialty';
    }
    if (!formData.country) return 'Please select your country';
    if (formData.country === 'Other' && !formData.countryOther.trim()) {
      return 'Please specify your country';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      // Check if email already exists in Firebase Auth
      const emailExists = await checkEmailExists(formData.email);
      if (emailExists) {
        setLoading(false);
        setError('This email is already registered in our system.');
        setExistingEmail(formData.email);
        return;
      }

      // Prepare user data
      // Membership ID will be auto-generated in registerUser function
      const fullName = `${formData.title} ${formData.firstName} ${formData.lastName}`;
      const workplace = formData.workplace === 'Other' ? formData.workplaceOther : formData.workplace;
      const finalWorkplace = formData.workplace === 'Government MOH' 
        ? `${formData.workplace} - ${formData.governmentHospital}` 
        : workplace;
      const designation = formData.designation === 'Other' ? formData.designationOther : formData.designation;
      const specialty = formData.specialty === 'Other' ? formData.specialtyOther : formData.specialty;
      const country = formData.country === 'Other' ? formData.countryOther : formData.country;

      const userData = {
        title: formData.title,
        firstName: formData.firstName,
        lastName: formData.lastName,
        name: fullName,
        email: formData.email,
        phone: formData.phone,
        workplace: finalWorkplace,
        designation: designation,
        specialty: specialty,
        country: country,
        role: 'member'
      };

      await registerUser(formData.email, formData.password, userData);

      setSuccess('Registration successful! Please check your email to verify your account.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      console.error('Registration error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('This email address is already registered. Please use a different email or try logging in.');
        setExistingEmail(formData.email);
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Your password is too weak. Please use at least 6 characters with a mix of letters and numbers.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again.');
      } else if (err.code === 'auth/operation-not-allowed') {
        setError('Registration is currently disabled. Please contact support.');
      } else {
        setError('Unable to complete registration. Please try again or contact support if the problem persists.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await resetPassword(existingEmail);
      setSuccess(`Password reset email sent to ${existingEmail}. Please check your inbox.`);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address. Please contact support.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError('Unable to send password reset email. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show password reset form if email already exists
  if (showPasswordReset) {
    return (
      <div className="register-page">
        <div className="register-container">
          <Card className="register-card">
            <div className="register-header">
              <div className="register-logo">
                <img src="/logo.svg" alt="KOS Logo" className="logo-image" />
              </div>
              <h1 className="register-title">Account Already Exists</h1>
              <p className="register-subtitle">Reset your password to continue</p>
            </div>

            <form onSubmit={handlePasswordReset} className="register-form">
              <div className="form-group">
                <label htmlFor="existingEmail" className="form-label">
                  Email Address
                </label>
                <Input
                  id="existingEmail"
                  type="email"
                  value={existingEmail}
                  disabled
                  className="input-disabled"
                />
                <p className="form-helper-text">
                  This email is already registered. We'll send you a password reset link.
                </p>
              </div>

              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              {success && (
                <div className="alert alert-success">
                  {success}
                </div>
              )}

              <Button 
                type="submit" 
                className="submit-button"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Password Reset Link'}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="back-button"
                onClick={() => {
                  setShowPasswordReset(false);
                  setError('');
                  setSuccess('');
                  setExistingEmail('');
                }}
              >
                Try Different Email
              </Button>

              <div className="form-footer">
                <p>
                  Already have your password?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="link-button"
                  >
                    Login here
                  </button>
                </p>
              </div>
            </form>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="register-page">
      <div className="register-container">
        <Card className="register-card">
          <div className="register-header">
            <div className="register-logo">
              <img src="/logo.svg" alt="KOS Logo" className="logo-image" />
            </div>
            <h1 className="register-title">Create KOS Account</h1>
            <p className="register-subtitle">Join the Kuwait Ophthalmological Society</p>
          </div>

          <form onSubmit={handleSubmit} className="register-form">
            {/* Title */}
            <div className="form-group">
              <label htmlFor="title" className="form-label">Title *</label>
              <select
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="">Select Title</option>
                {titles.map(title => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </select>
            </div>

            {/* First Name & Last Name */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName" className="form-label">First Name *</label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName" className="form-label">Last Name *</label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email *</label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
              />
            </div>

            {/* Phone */}
            <div className="form-group">
              <label htmlFor="phone" className="form-label">Phone *</label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                required
              />
            </div>

            {/* Password */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password" className="form-label">Password *</label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">Confirm Password *</label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  required
                />
              </div>
            </div>

            {/* Workplace */}
            <div className="form-group">
              <label htmlFor="workplace" className="form-label">Workplace *</label>
              <select
                id="workplace"
                name="workplace"
                value={formData.workplace}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="">Select Workplace</option>
                {workplaces.map(wp => (
                  <option key={wp} value={wp}>{wp}</option>
                ))}
              </select>
            </div>

            {/* Government Hospital (conditional) */}
            {formData.workplace === 'Government MOH' && (
              <div className="form-group">
                <label htmlFor="governmentHospital" className="form-label">Government Hospital *</label>
                <select
                  id="governmentHospital"
                  name="governmentHospital"
                  value={formData.governmentHospital}
                  onChange={handleChange}
                  className="form-select"
                  required
                >
                  <option value="">Select Hospital</option>
                  {governmentHospitals.map(hospital => (
                    <option key={hospital} value={hospital}>{hospital}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Workplace Other (conditional) */}
            {formData.workplace === 'Other' && (
              <div className="form-group">
                <label htmlFor="workplaceOther" className="form-label">Specify Workplace *</label>
                <Input
                  id="workplaceOther"
                  name="workplaceOther"
                  type="text"
                  value={formData.workplaceOther}
                  onChange={handleChange}
                  placeholder="Enter workplace name"
                  required
                />
              </div>
            )}

            {/* Designation */}
            <div className="form-group">
              <label htmlFor="designation" className="form-label">Designation *</label>
              <select
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="">Select Designation</option>
                {designations.map(des => (
                  <option key={des} value={des}>{des}</option>
                ))}
              </select>
            </div>

            {/* Designation Other (conditional) */}
            {formData.designation === 'Other' && (
              <div className="form-group">
                <label htmlFor="designationOther" className="form-label">Specify Designation *</label>
                <Input
                  id="designationOther"
                  name="designationOther"
                  type="text"
                  value={formData.designationOther}
                  onChange={handleChange}
                  placeholder="Enter designation"
                  required
                />
              </div>
            )}

            {/* Specialty */}
            <div className="form-group">
              <label htmlFor="specialty" className="form-label">Specialty *</label>
              <select
                id="specialty"
                name="specialty"
                value={formData.specialty}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="">Select Specialty</option>
                {specialties.map(spec => (
                  <option key={spec} value={spec}>{spec}</option>
                ))}
              </select>
            </div>

            {/* Specialty Other (conditional) */}
            {formData.specialty === 'Other' && (
              <div className="form-group">
                <label htmlFor="specialtyOther" className="form-label">Specify Specialty *</label>
                <Input
                  id="specialtyOther"
                  name="specialtyOther"
                  type="text"
                  value={formData.specialtyOther}
                  onChange={handleChange}
                  placeholder="Enter specialty"
                  required
                />
              </div>
            )}

            {/* Country */}
            <div className="form-group">
              <label htmlFor="country" className="form-label">Country *</label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="">Select Country</option>
                {countries.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Country Other (conditional) */}
            {formData.country === 'Other' && (
              <div className="form-group">
                <label htmlFor="countryOther" className="form-label">Specify Country *</label>
                <Input
                  id="countryOther"
                  name="countryOther"
                  type="text"
                  value={formData.countryOther}
                  onChange={handleChange}
                  placeholder="Enter country name"
                  required
                />
              </div>
            )}

            {/* Error and Success Messages */}
            {error && (
              <div className="alert alert-error">
                {error}
                {existingEmail && (
                  <button
                    type="button"
                    className="reset-password-btn"
                    onClick={async () => {
                      setLoading(true);
                      setError('');
                      try {
                        await resetPassword(existingEmail);
                        setSuccess(`Password reset email sent to ${existingEmail}. Please check your inbox.`);
                        setTimeout(() => {
                          navigate('/login');
                        }, 2000);
                      } catch (err) {
                        setError('Failed to send reset email. Please try again.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Password Reset Email'}
                  </button>
                )}
              </div>
            )}

            {success && (
              <div className="alert alert-success">
                {success}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>

            <div className="register-footer">
              <p className="text-center">
                Already have an account?{' '}
                <a href="/login" className="link-primary">
                  Login here
                </a>
              </p>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Register;
